/**
 * Host-side half of `useNavigation().openEntity()` — the one call a sandboxed
 * Page uses to open OR create any CRM record the UI can open or create.
 *
 * The Page sends `{entity, payload}`; `payload.id` means "open that record",
 * no id means "open the create form, prefilled from the rest of the payload".
 * Everything a Page can send is validated in `pageEntities.ts` before it gets
 * here.
 *
 * Three rules shape this module:
 *
 *   1. **Open-existing reuses `openCrmEntity`** — the dispatcher the chat
 *      surfaces already use. No second copy of the entity→editor routing.
 *      The exception is the handful of entities `openCrmEntity` sends to
 *      `window.open`: from a Page those navigate the host in the SAME tab,
 *      because a sandboxed iframe must never make the host open a popup.
 *   2. **The Page sends ids; the host hydrates.** `CreateCall`, `CreateTicket`
 *      and `EditContact` want a `{id, name}` client, `openEditOrder` wants a
 *      bare `clientId`, `editActivity` fetches the client itself. A Page can't
 *      be expected to know which, so we fetch the Client/Contact once and hand
 *      each editor the shape it actually wants. The fetch doubles as an
 *      existence check — a bogus id becomes an error toast, not a broken modal.
 *   3. **Nothing fails silently.** A Page button that does nothing is
 *      indistinguishable from a broken app, so every refusal is a user-visible
 *      notification plus a console error (this path is fire-and-forget; there
 *      is no promise for the Page to catch).
 *
 * Loaded lazily by `sandboxBridge` — it pulls in the modal/drawer services and
 * the `Tools` global, neither of which exists at module-eval time in the
 * bridge's dependency-free unit test.
 */

import { openDrawer } from 'Services/Drawer';
import openModal from 'App/services/Modal';
import openAgreement from 'App/helpers/openSubscriptionHelper';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditContact } from 'App/components/EditContact/openEditContact';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { openNewMailWithContact } from 'App/helpers/mailHelpers';
import { openCrmEntity } from 'App/shadcn/pages/Assistant/openCrmEntity';
import { shouldIgnoreRepeatOpen } from 'App/shadcn/pages/Assistant/entityOpenGuard';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import NotificationService from 'App/babel/NotificationService';
import ClientResource from 'App/resources/Client';
import ContactResource from 'App/resources/Contact';
import AppService from 'App/legacy/AppService';
import logError from 'App/babel/helpers/logError';
import { sanitizePageEntityPayload, type PageEntity } from 'App/sandboxed-page/pageEntities';
import type { ClientCreateRights } from 'App/resources/Model/Client';
import type Contact from 'App/resources/Model/Contact';

// The editors each want a different slice of the company — `CreateSubscription`
// the price list, `EditTicket` the users and journey step — so the ref carries
// the widest slice we can always produce: the one a Contact's own client has.
type ClientRef = Contact['client'];

type Refs = {
	client: ClientRef | null;
	contact: Contact | null;
	createRights: ClientCreateRights | null;
};

/**
 * Entities whose "open" is a route, not a modal. `openCrmEntity` reaches these
 * through `window.open` (a new tab), which the Page sandbox forbids — so they
 * navigate the host in place instead, via the bridge's own `navigate`.
 */
const ROUTE_STATES: Partial<Record<PageEntity, string>> = {
	client: 'account.dashboard',
	contact: 'contact.dashboard',
	segment: 'segment',
	campaign: 'campaign.dashboard'
};

/**
 * The create right each create path needs. Absent = the editor governs access
 * itself (support tickets, mail). Checked only when the payload carries a
 * `clientId`, since the right lives on the client.
 */
const CREATE_RIGHT: Partial<Record<PageEntity, keyof ClientCreateRights>> = {
	contact: 'Contact',
	activity: 'Activity',
	todo: 'Activity',
	phonecall: 'Activity',
	appointment: 'Appointment',
	order: 'Order',
	agreement: 'Agreement'
};

const notifyFailure = (titleKey: string): void => {
	NotificationService.add({
		style: NotificationService.style.ERROR,
		icon: 'exclamation-circle',
		title: titleKey
	});
};

/** A Page asked for something the host can't do — report it both ways. */
const refuse = (titleKey: string, reason: string): void => {
	// eslint-disable-next-line no-console
	console.error(`[pageEntityActions] ${reason}`);
	notifyFailure(titleKey);
};

/**
 * Fetch whatever the payload referenced. A failed lookup is fatal for the
 * action: continuing would open an editor whose relation silently isn't set,
 * which is the "reports success, did nothing" failure we're avoiding.
 */
const resolveRefs = async (prefill: Record<string, string | number>): Promise<Refs> => {
	const clientId = typeof prefill.clientId === 'number' ? prefill.clientId : undefined;
	const contactId = typeof prefill.contactId === 'number' ? prefill.contactId : undefined;

	const [clientRes, contactRes] = await Promise.all([
		clientId ? ClientResource.get(clientId) : Promise.resolve(null),
		contactId ? ContactResource.get(contactId) : Promise.resolve(null)
	]);

	const contact = contactRes?.data ?? null;
	const client = clientRes?.data ?? null;
	// A contact carries its own company — use it when the Page only named the
	// contact, so `openEntity('mail', {contactId})` still relates to the client.
	const effectiveClient = client ?? contact?.client ?? null;

	return {
		client: effectiveClient,
		contact,
		createRights: (client as { createRights?: ClientCreateRights } | null)?.createRights ?? null
	};
};

const openExisting = (entity: PageEntity, id: number, navigate: (url: string) => void): void => {
	const stateName = ROUTE_STATES[entity];
	if (stateName) {
		const customerId = AppService.getCustomerId();
		const href = Tools.$state?.href?.(stateName, { id, customerId: customerId != null ? String(customerId) : '' });
		if (!href) {
			refuse('aiWorkspace.pages.openEntityFailed', `no route for ${entity}`);
			return;
		}
		navigate(href);
		return;
	}

	// `openCrmEntity` applies its own repeat-open guard, so this path must not
	// touch the guard first — doing so would make the dispatcher swallow the
	// very click that armed it.
	if (!openCrmEntity(entity, id)) {
		refuse('aiWorkspace.pages.openEntityFailed', `${entity} is not openable by id`);
	}
};

/** Escape a Page-authored draft and keep its line breaks. */
const textToHtml = (text: string): string =>
	text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/\n/g, '<br>');

const createEntity = async (
	entity: PageEntity,
	prefill: Record<string, string | number>,
	refs: Refs
): Promise<void> => {
	const { client, contact } = refs;
	const description = typeof prefill.description === 'string' ? prefill.description : undefined;
	const contacts = contact ? [{ id: contact.id }] : undefined;

	switch (entity) {
		case 'contact':
			openEditContact({
				account: client ?? undefined,
				clientId: client?.id,
				contact: {
					firstName: prefill.firstName as string | undefined,
					lastName: prefill.lastName as string | undefined,
					title: prefill.title as string | undefined,
					email: prefill.email as string | undefined,
					phone: prefill.phone as string | undefined
				}
			});
			return;

		case 'order':
			openEditOrder({
				clientId: client?.id,
				contactId: contact?.id,
				description,
				notes: prefill.notes as string | undefined,
				type: (prefill.type as 'order' | 'opportunity' | undefined) ?? 'order'
			});
			return;

		case 'agreement':
			if (!client) {
				refuse('aiWorkspace.pages.openEntityFailed', 'agreement create needs a clientId');
				return;
			}
			// Mirrors the client card's create menu: the new subscription modal
			// where the account has it, the Angular one otherwise.
			if (hasSoftDeployAccess('SUBSCRIPTION_MODAL')) {
				openModal('CreateSubscription', { client, createdFrom: 'page', dontWait: true });
			} else {
				Tools.$upModal.open('editAgreement', { accountId: client.id });
			}
			return;

		case 'activity':
			// `editActivity` merges this into a fresh activity and hydrates the
			// client itself, so bare ids are enough here.
			Tools.$upModal.open('editActivity', {
				activity: {
					...(client ? { client: { id: client.id } } : {}),
					...(contacts ? { contacts } : {}),
					...(description ? { description } : {})
				}
			});
			return;

		case 'todo':
			openDrawer('CreateTodo', { client, contact, description });
			return;

		case 'phonecall':
			openDrawer('CreateCall', { client, contact, initialDescription: description });
			return;

		case 'appointment':
			openEditAppointment({
				appointment: {
					...(client ? { client: { id: client.id, name: client.name } } : {}),
					...(contacts ? { contacts } : {}),
					...(description ? { description } : {})
				}
			});
			return;

		case 'ticket':
			if (!client) {
				refuse('aiWorkspace.pages.openEntityFailed', 'ticket create needs a clientId');
				return;
			}
			openDrawer('EditTicket', { client, contact, description });
			return;

		case 'mail':
			if (!contact?.email) {
				refuse('aiWorkspace.pages.openEntityNoEmail', 'mail create needs a contact with an email');
				return;
			}
			openNewMailWithContact(
				{ id: contact.id, name: contact.name, email: contact.email, client: contact.client ?? undefined },
				{
					subject: prefill.subject as string | undefined,
					// Plain text only: this lands in a WYSIWYG composer the rep
					// reads before sending, and accepting authored HTML there
					// needs a sanitisation story it doesn't have yet.
					body: typeof prefill.body === 'string' ? textToHtml(prefill.body) : undefined
				}
			);
			return;

		default:
			// Declared creatable in `pageEntities.ts` but with no case here —
			// a developer bug, reported the same way `pageToolDispatcher` reports
			// a page-allowed tool with no dispatcher case.
			refuse('aiWorkspace.pages.openEntityFailed', `no create path implemented for ${entity}`);
	}
};

/**
 * Act on one `page.openEntity` request. `navigate` is the bridge's own
 * same-origin hash navigation, passed in so this module owns no navigation
 * policy of its own.
 */
export async function openPageEntity(
	entity: PageEntity,
	rawPayload: unknown,
	navigate: (url: string) => void
): Promise<void> {
	const { id, prefill, unknownKeys, invalidKeys } = sanitizePageEntityPayload(entity, rawPayload);

	// Dropped fields are a bug in the Page, not a reason to refuse the click —
	// say so loudly and open with what was valid.
	if (unknownKeys.length || invalidKeys.length) {
		// eslint-disable-next-line no-console
		console.error(
			`[pageEntityActions] openEntity('${entity}') ignored fields —` +
				` unknown: [${unknownKeys.join(', ')}], invalid: [${invalidKeys.join(', ')}]`
		);
	}

	if (id !== undefined) {
		openExisting(entity, id, navigate);
		return;
	}

	// Guarded here rather than in `openCrmEntity`: a create has no id to key on,
	// and a double-clicked button would otherwise stack two identical forms.
	if (shouldIgnoreRepeatOpen(`create-${entity}`, 0)) {
		return;
	}

	try {
		const refs = await resolveRefs(prefill);

		if ((prefill.clientId || prefill.contactId) && !refs.client && !refs.contact) {
			refuse('aiWorkspace.pages.openEntityFailed', `could not load the client/contact for ${entity}`);
			return;
		}

		const right = CREATE_RIGHT[entity];
		const rightKey = right === 'Order' && prefill.type === 'opportunity' ? 'Opportunity' : right;
		if (rightKey && refs.createRights && refs.createRights[rightKey] === false) {
			refuse('aiWorkspace.pages.openEntityNoRights', `missing ${rightKey} create right`);
			return;
		}

		await createEntity(entity, prefill, refs);
	} catch (err) {
		logError(err, `Failed to open the ${entity} form from a Page`);
		notifyFailure('aiWorkspace.pages.openEntityFailed');
	}
}
